import React from "react";
import RegistrationGuard from "@/components/auth/RegistrationGuard";
import PsychologistProfileModal from "@/components/home/PsychologistProfileModal";
import LoadingState from "@/components/schedule/LoadingState";
import ErrorState from "@/components/schedule/ErrorState";
import { usePsychologistDirectory } from "@/hooks/usePsychologistDirectory";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Star, User, Calendar, Filter, Search, Grid, List, SortAsc, Heart, Share2, Languages } from "lucide-react";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const PsychologistDirectory = () => {
  console.log('PsychologistDirectory - Component rendering');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState("all");
  const [priceRange, setPriceRange] = useState("all");
  const [sortBy, setSortBy] = useState("rating");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  
  const {
    psychologists,
    isLoading,
    queryError,
    selectedPsychologistForModal,
    isProfileModalOpen,
    handleViewProfile,
    handleViewFullProfile,
    handleScheduleNow,
    closeModal
  } = usePsychologistDirectory();

  console.log('PsychologistDirectory - Hook data loaded', {
    psychologistsCount: psychologists.length,
    isLoading,
    hasError: !!queryError
  });

  // Extract unique languages dynamically from psychologists data
  const availableLanguages = useMemo(() => {
    const languageSet = new Set<string>();
    psychologists.forEach(psychologist => {
      if (psychologist.languages && Array.isArray(psychologist.languages)) {
        psychologist.languages.forEach(lang => {
          if (lang && typeof lang === 'string' && lang.trim()) {
            languageSet.add(lang.trim());
          }
        });
      }
    });
    return Array.from(languageSet).sort();
  }, [psychologists]);

  const filteredAndSortedPsychologists = useMemo(() => {
    let filtered = psychologists.filter(psychologist => {
      const matchesSearch = !searchQuery || psychologist.name.toLowerCase().includes(searchQuery.toLowerCase()) || psychologist.specialties && psychologist.specialties.some((spec: string) => spec.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesSpecialty = selectedSpecialties.length === 0 || psychologist.specialties && psychologist.specialties.some((spec: string) => selectedSpecialties.includes(spec));
      const matchesLanguage = selectedLanguage === 'all' || psychologist.languages && psychologist.languages.includes(selectedLanguage);
      const matchesPrice = priceRange === 'all' || priceRange === '0-150' && psychologist.price <= 150 || priceRange === '150-250' && psychologist.price > 150 && psychologist.price <= 250 || priceRange === '250-350' && psychologist.price > 250 && psychologist.price <= 350 || priceRange === '350+' && psychologist.price > 350;
      const matchesAvailability = availabilityFilter === 'all' || availabilityFilter === 'online' && psychologist.isOnline || availabilityFilter === 'today' && psychologist.hasAvailabilityToday;
      return matchesSearch && matchesSpecialty && matchesLanguage && matchesPrice && matchesAvailability;
    });

    // Sort filtered results
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'price-low':
          return a.price - b.price;
        case 'price-high':
          return b.price - a.price;
        case 'name':
          return a.name.localeCompare(b.name);
        case 'experience':
          return (b.total_reviews || 0) - (a.total_reviews || 0);
        default:
          return 0;
      }
    });
    return filtered;
  }, [psychologists, searchQuery, selectedSpecialties, selectedLanguage, priceRange, availabilityFilter, sortBy]);

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedSpecialties([]);
    setSelectedLanguage("all");
    setPriceRange("all");
    setAvailabilityFilter("all");
    setSortBy("rating");
  };

  const hasActiveFilters = searchQuery || selectedSpecialties.length > 0 || selectedLanguage !== "all" || priceRange !== "all" || availabilityFilter !== "all";

  if (isLoading) {
    console.log('PsychologistDirectory - Showing loading state');
    return <LoadingState />;
  }
  if (queryError) {
    console.error('PsychologistDirectory - Showing error state', queryError);
    return <ErrorState />;
  }
  console.log('PsychologistDirectory - Rendering main content', {
    filteredCount: filteredAndSortedPsychologists.length,
    viewMode
  });

  return <RegistrationGuard>
      <div className="min-h-screen bg-background">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-16">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">Encontre o
Psicólogo Ideal</h1>
              <p className="text-xl md:text-2xl mb-8 text-blue-100">
                Conecte-se com profissionais qualificados e inicie sua jornada de bem-estar mental
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
                <div className="text-center">
                  <div className="text-3xl font-bold">{psychologists.length}+</div>
                  <div className="text-blue-100">Psicólogos Disponíveis</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">4.8★</div>
                  <div className="text-blue-100">Avaliação Média</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">24h</div>
                  <div className="text-blue-100">Disponibilidade</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-8">
          <Card className="mb-8 bg-card border-border">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2 text-card-foreground">
                    <Filter className="h-5 w-5" />
                    Filtros e Busca
                  </CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Use os filtros para encontrar o psicólogo ideal para suas necessidades
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={viewMode} onValueChange={(value: "grid" | "list") => setViewMode(value)}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="grid">
                        <div className="flex items-center gap-2">
                          <Grid className="h-4 w-4" />
                          Grade
                        </div>
                      </SelectItem>
                      <SelectItem value="list">
                        <div className="flex items-center gap-2">
                          <List className="h-4 w-4" />
                          Lista
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar por nome, especialidade ou abordagem..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 bg-background border-input text-foreground" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <Select value={priceRange} onValueChange={setPriceRange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Faixa de preço" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os preços</SelectItem>
                    <SelectItem value="0-150">Até R$ 150</SelectItem>
                    <SelectItem value="150-250">R$ 150 - R$ 250</SelectItem>
                    <SelectItem value="250-350">R$ 250 - R$ 350</SelectItem>
                    <SelectItem value="350+">Acima de R$ 350</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Disponibilidade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as disponibilidades</SelectItem>
                    <SelectItem value="online">Online agora</SelectItem>
                    <SelectItem value="today">Disponível hoje</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger>
                    <SelectValue placeholder="Ordenar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rating">Melhor avaliados</SelectItem>
                    <SelectItem value="price-low">Menor preço</SelectItem>
                    <SelectItem value="price-high">Maior preço</SelectItem>
                    <SelectItem value="name">Nome A-Z</SelectItem>
                    <SelectItem value="experience">Mais experientes</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
                  <SelectTrigger>
                    <SelectValue placeholder="Idioma" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os idiomas</SelectItem>
                    {availableLanguages.map((language) => (
                      <SelectItem key={language} value={language}>
                        <div className="flex items-center gap-2">
                          <Languages className="h-4 w-4" />
                          {language}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {hasActiveFilters && <Button variant="outline" onClick={handleClearFilters}>
                    Limpar Filtros
                  </Button>}
              </div>

              {/* Show selected language filter */}
              {selectedLanguage !== "all" && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-muted-foreground">Idioma selecionado:</span>
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Languages className="h-3 w-3" />
                    {selectedLanguage}
                    <button
                      onClick={() => setSelectedLanguage("all")}
                      className="ml-1 hover:text-destructive"
                    >
                      ×
                    </button>
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                Psicólogos Disponíveis
              </h2>
              <p className="text-muted-foreground">
                {filteredAndSortedPsychologists.length} profissionais encontrados
              </p>
            </div>
            <div className="flex items-center gap-2">
              <SortAsc className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                Ordenado por: {sortBy === 'rating' ? 'Avaliação' : sortBy === 'price-low' ? 'Menor preço' : sortBy === 'price-high' ? 'Maior preço' : sortBy === 'name' ? 'Nome' : 'Experiência'}
              </span>
            </div>
          </div>

          {filteredAndSortedPsychologists.length === 0 ? <Card className="text-center py-12 bg-card border-border">
              <CardContent>
                <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-card-foreground mb-2">
                  Nenhum psicólogo encontrado
                </h3>
                <p className="text-muted-foreground mb-4">
                  Tente ajustar os filtros ou buscar por outros termos
                </p>
                <Button onClick={handleClearFilters} variant="outline">
                  Limpar Filtros
                </Button>
              </CardContent>
            </Card> : viewMode === "grid" ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAndSortedPsychologists.map(psychologist => <Card key={psychologist.id} className="hover:shadow-lg transition-shadow duration-300 group bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-4">
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-muted relative">
                          {psychologist.photoUrl ? <img src={psychologist.photoUrl} alt={psychologist.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center">
                              <User className="h-8 w-8 text-muted-foreground" />
                            </div>}
                          {psychologist.isOnline && <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 border-2 border-background rounded-full"></div>}
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg text-card-foreground">
                            {psychologist.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">CRP: {psychologist.crp}</p>
                          <div className="flex items-center mt-1">
                            <Star className="h-4 w-4 text-yellow-400 fill-current" />
                            <span className="text-sm ml-1 text-card-foreground">{psychologist.rating?.toFixed(1) || '5.0'}</span>
                            <span className="text-xs text-muted-foreground ml-1">
                              ({psychologist.total_reviews || 0} avaliações)
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                          <Heart className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0">
                          <Share2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                      {psychologist.shortBio || psychologist.bio}
                    </p>
                    
                    <div className="flex flex-wrap gap-1 mb-4">
                      {psychologist.specialties?.slice(0, 3).map((specialty, index) => <Badge key={index} className="bg-blue-500 hover:bg-blue-600 text-white text-xs">
                          {specialty}
                        </Badge>)}
                      {psychologist.specialties && psychologist.specialties.length > 3 && <Badge className="bg-blue-500 hover:bg-blue-600 text-white text-xs">
                          +{psychologist.specialties.length - 3}
                        </Badge>}
                    </div>

                    {psychologist.languages && psychologist.languages.length > 0 && <div className="flex flex-wrap gap-1 mb-4">
                        <Languages className="h-3 w-3 text-muted-foreground mt-0.5" />
                        {psychologist.languages.slice(0, 2).map((language, index) => <Badge key={index} className="bg-purple-500 hover:bg-purple-600 text-white text-xs">
                            {language}
                          </Badge>)}
                        {psychologist.languages.length > 2 && <Badge className="bg-purple-500 hover:bg-purple-600 text-white text-xs">
                            +{psychologist.languages.length - 2}
                          </Badge>}
                      </div>}

                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <span className="text-2xl font-bold text-primary">
                          R$ {psychologist.price?.toFixed(2).replace('.', ',')}
                        </span>
                        <span className="text-sm text-muted-foreground block">por consulta</span>
                      </div>
                      {psychologist.isOnline && <Badge className="bg-green-500">
                          Online agora
                        </Badge>}
                    </div>

                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleViewProfile(psychologist.id)} className="flex-1">
                        Ver Perfil
                      </Button>
                      <Button onClick={() => handleScheduleNow(psychologist.id)} size="sm" className="flex-1 bg-green-500 hover:bg-green-600">
                        <Calendar className="h-4 w-4 mr-1" />
                        Agendar
                      </Button>
                    </div>
                  </CardContent>
                </Card>)}
            </div> : <div className="space-y-4">
              {filteredAndSortedPsychologists.map(psychologist => <Card key={psychologist.id} className="hover:shadow-md transition-shadow bg-card border-border">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6 flex-1">
                        <div className="w-20 h-20 rounded-full overflow-hidden bg-muted relative">
                          {psychologist.photoUrl ? <img src={psychologist.photoUrl} alt={psychologist.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center">
                              <User className="h-10 w-10 text-muted-foreground" />
                            </div>}
                          {psychologist.isOnline && <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 border-2 border-background rounded-full"></div>}
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-xl text-card-foreground">
                              {psychologist.name}
                            </h3>
                            {psychologist.isOnline && <Badge className="bg-green-500 text-xs">Online</Badge>}
                          </div>
                          
                          <div className="flex items-center gap-4 mb-2">
                            <div className="flex items-center">
                              <Star className="h-4 w-4 text-yellow-400 fill-current" />
                              <span className="text-sm ml-1 font-medium text-card-foreground">
                                {psychologist.rating?.toFixed(1) || '5.0'}
                              </span>
                              <span className="text-xs text-muted-foreground ml-1">
                                ({psychologist.total_reviews || 0} avaliações)
                              </span>
                            </div>
                            <span className="text-sm text-muted-foreground">CRP: {psychologist.crp}</span>
                          </div>
                          
                          <p className="text-muted-foreground mb-3 line-clamp-2">
                            {psychologist.shortBio || psychologist.bio}
                          </p>
                          
                          <div className="flex flex-wrap gap-1">
                            {psychologist.specialties?.slice(0, 4).map((specialty, index) => <Badge key={index} className="bg-blue-500 hover:bg-blue-600 text-white text-xs">
                                {specialty}
                              </Badge>)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-col items-end gap-3">
                        <div className="text-right">
                          <div className="text-2xl font-bold text-primary">
                            R$ {psychologist.price?.toFixed(2).replace('.', ',')}
                          </div>
                          <div className="text-sm text-muted-foreground">por consulta</div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleViewProfile(psychologist.id)}>
                            Ver Perfil
                          </Button>
                          <Button onClick={() => handleScheduleNow(psychologist.id)} size="sm" className="bg-green-500 hover:bg-green-600">
                            <Calendar className="h-4 w-4 mr-1" />
                            Agendar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>}
        </div>

        {selectedPsychologistForModal && <PsychologistProfileModal isOpen={isProfileModalOpen} onClose={closeModal} psychologist={selectedPsychologistForModal} onBookConsultation={handleScheduleNow} onViewFullProfile={handleViewFullProfile} />}
      </div>
    </RegistrationGuard>;
};
export default PsychologistDirectory;
